import { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Plus, CalendarClock } from "lucide-react";

const SERVICES = [
  "Tax Planning Discovery",
  "Retirement Planning Review",
  "Estate & Legacy Planning",
  "Wealth Management Consultation",
  "Quarterly Check-In",
];
const TIMES = ["9:00 AM", "10:00 AM", "11:00 AM", "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM"];

function nextDay(d = 2) {
  const x = new Date(); x.setDate(x.getDate() + d);
  return x.toISOString().slice(0, 10);
}

export default function AppointmentsPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    service: SERVICES[0],
    preferred_date: nextDay(2),
    preferred_time: "10:00 AM",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const load = () =>
    api.get("/appointments/me").then((r) => setItems(r.data.appointments || [])).catch(() => {});

  useEffect(() => { load(); }, []);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/appointments/me", form);
      toast.success("Appointment requested");
      setShowForm(false);
      setForm({ service: SERVICES[0], preferred_date: nextDay(2), preferred_time: "10:00 AM", notes: "" });
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = items.filter((a) => a.preferred_date >= today);
  const past = items.filter((a) => a.preferred_date < today);

  return (
    <div data-testid="page-appointments" className="space-y-8">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#8E6A2B] mb-3">— Appointments</div>
          <h1 className="font-serif text-5xl text-[#0F292F] font-light tracking-tight">Schedule & history</h1>
        </div>
        <button
          data-testid="appt-new-btn"
          onClick={() => setShowForm((v) => !v)}
          className="btn-primary"
        >
          <Plus size={16} /> {showForm ? "Cancel" : "Request Appointment"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="appt-form" className="bg-white border border-black/10 p-8 grid md:grid-cols-2 gap-6">
          <label className="block">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Service *</div>
            <select data-testid="appt-service" required className="he-input bg-transparent" value={form.service} onChange={onChange("service")}>
              {SERVICES.map((s) => (<option key={s}>{s}</option>))}
            </select>
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Preferred Date *</div>
            <input type="date" data-testid="appt-date" required min={nextDay(1)} className="he-input" value={form.preferred_date} onChange={onChange("preferred_date")} />
          </label>
          <label className="block">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Preferred Time *</div>
            <select data-testid="appt-time" required className="he-input bg-transparent" value={form.preferred_time} onChange={onChange("preferred_time")}>
              {TIMES.map((t) => (<option key={t}>{t}</option>))}
            </select>
          </label>
          <label className="block md:col-span-2">
            <div className="text-xs uppercase tracking-[0.2em] text-[#52525B] mb-2">Notes</div>
            <textarea data-testid="appt-notes" rows={3} className="he-input resize-none" value={form.notes} onChange={onChange("notes")} placeholder="Anything specific to cover..." />
          </label>
          <div className="md:col-span-2 flex justify-end">
            <button type="submit" data-testid="appt-submit" disabled={submitting} className="btn-accent disabled:opacity-60">
              {submitting ? "Submitting..." : "Submit Request"}
            </button>
          </div>
        </form>
      )}

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-4">Upcoming</div>
        {upcoming.length === 0 ? (
          <div className="bg-white border border-black/10 p-8 text-[#52525B]">No upcoming appointments.</div>
        ) : (
          <div className="space-y-3">
            {upcoming.map((a) => <ApptRow key={a.id} a={a} />)}
          </div>
        )}
      </div>

      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-[#8E6A2B] mb-4">Past</div>
        {past.length === 0 ? (
          <div className="bg-white border border-black/10 p-8 text-[#52525B]">No past appointments.</div>
        ) : (
          <div className="space-y-3">
            {past.map((a) => <ApptRow key={a.id} a={a} />)}
          </div>
        )}
      </div>
    </div>
  );
}

function ApptRow({ a }) {
  return (
    <div data-testid={`appt-row-${a.id}`} className="bg-white border border-black/10 p-6 flex items-start gap-6">
      <div className="w-12 h-12 bg-[#0F292F] text-[#CFA968] flex items-center justify-center flex-shrink-0">
        <CalendarClock size={20} strokeWidth={1.2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-serif text-2xl text-[#0F292F]">{a.service}</div>
        <div className="text-sm text-[#52525B] mt-1">
          {new Date(a.preferred_date).toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
          {" · "}{a.preferred_time}
        </div>
        {a.notes && <div className="text-sm text-[#52525B] italic mt-2">"{a.notes}"</div>}
      </div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#0F292F] bg-[#8E6A2B]/15 px-3 py-1 self-start">
        {a.status}
      </div>
    </div>
  );
}
